import { useState, useEffect, useCallback, useRef } from 'react';
import type { PromoItem } from '../../components/home/promo/PromoCard';

export interface UsePromoCarouselOptions {
  items: PromoItem[];
  autoPlayInterval?: number;
  pauseOnHover?: boolean;
}

export function usePromoCarousel(options: UsePromoCarouselOptions) {
  const { items, autoPlayInterval = 10000, pauseOnHover = true } = options;
  const [isPaused, setIsPaused] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goTo = useCallback((index: number) => {
    if (items.length === 0) return;
    const normalized = ((index % items.length) + items.length) % items.length;
    setCurrentIndex(normalized);
  }, [items.length]);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
  }, [items.length]);

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % items.length);
  }, [items.length]);

  // 每 10 秒自动向右滚动一次
  useEffect(() => {
    if (items.length <= 1 || (pauseOnHover && isPaused)) return;
    timerRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % items.length);
    }, autoPlayInterval);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [items.length, isPaused, autoPlayInterval, pauseOnHover]);

  return {
    isPaused,
    setIsPaused,
    currentIndex,
    goTo,
    goPrev,
    goNext,
  };
}
